/**
 * Size Chart Modal — assets/size-chart.js
 *
 * Handles:
 *  1. Opening / closing modals defined via size-chart-modal.liquid
 *  2. Keyboard navigation (Escape to close, focus trap)
 *  3. Inline injection — injects a small trigger beside the size option label
 *     (works with Dawn, Debut, Refresh, and most modern themes)
 */

(function () {
  'use strict';

  // ----------------------------------------------------------------
  // SizeChartModal class — one instance per modal on the page
  // ----------------------------------------------------------------
  class SizeChartModal {
    constructor(modalEl) {
      this.modal = modalEl;
      this.dialog = modalEl.querySelector('.size-chart-modal__dialog');
      this.closeButtons = modalEl.querySelectorAll('[data-modal-close]');
      this.isOpen = false;

      this._onKeyDown = this._onKeyDown.bind(this);
      this._bindClose();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      // Remove hidden attr (we use visibility + opacity instead)
      this.modal.removeAttribute('hidden');

      // Force reflow so transition fires
      void this.modal.offsetWidth;

      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeyDown);

      // Move focus into dialog
      const firstFocusable = this.dialog.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) firstFocusable.focus();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;

      this.modal.classList.remove('is-open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeyDown);

      // Wait for transition to finish before hiding
      this.modal.addEventListener(
        'transitionend',
        () => {
          if (!this.isOpen) this.modal.setAttribute('hidden', '');
        },
        { once: true }
      );

      // Return focus to trigger
      const triggerId = this.modal.id.replace('-modal-', '-trigger-');
      const trigger = document.getElementById(triggerId);
      if (trigger) trigger.focus();
    }

    _bindClose() {
      this.closeButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
    }

    _onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }

      // Basic focus trap
      if (e.key === 'Tab') {
        const focusableEls = Array.from(
          this.dialog.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        );
        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  // ----------------------------------------------------------------
  // Inline injection — adds a small trigger beside the size label
  // ----------------------------------------------------------------
  function injectInlineTrigger(config, modalInstance) {
    if (!config.injectInline) return;

    /**
     * These selectors cover the most common Shopify theme patterns.
     * The block setting `inline_selector` can override all of these.
     *
     * Dawn theme:      .product-form__input--pill label,
     *                  .variant-picker__option-values .swatch__label
     * Debut theme:     .single-option-selector + label
     * Refresh/general: [data-option-name="Size"] .option__label
     */
    const defaultSelectors = [
      // Heading / label for the Size option — rendered just once
      '.product-form__input[data-section] .form__label',
      '[data-option-name="Size"] .form__label',
      '.variant-picker__option-info .form__label',
      // Legacy selectors
      '.selector-wrapper:has([data-option-index]) label',
    ];

    const customSelector = config.inlineSelector;

    // Try custom selector first; fall back through defaults
    let labelEl = null;

    if (customSelector) {
      labelEl = document.querySelector(customSelector);
    }

    if (!labelEl) {
      for (const sel of defaultSelectors) {
        try {
          const candidate = document.querySelector(sel);
          if (candidate) {
            // Attempt to confirm it's actually the "Size" label
            if (/size/i.test(candidate.textContent)) {
              labelEl = candidate;
              break;
            }
            // If not size-specific but it's the first matching label, store as fallback
            if (!labelEl) labelEl = candidate;
          }
        } catch (_) {
          // Selector may not be supported on older browsers
        }
      }
    }

    if (!labelEl) {
      // Last resort: find any <label> that contains the word "Size"
      labelEl = Array.from(document.querySelectorAll('label, .form__label')).find(
        (el) => /size/i.test(el.textContent)
      );
    }

    if (!labelEl) return; // Could not find a suitable anchor

    // Don't inject twice
    if (labelEl.querySelector('.size-chart-inline-trigger')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'size-chart-inline-trigger';
    btn.setAttribute('aria-controls', config.modalId);
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.innerHTML = `
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
        <rect x="0.75" y="0.75" width="11.5" height="11.5" rx="2" stroke="currentColor" stroke-width="1.2"/>
        <path d="M3 5h7M3 7.5h5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
      </svg>
      ${config.buttonLabel}`;

    btn.addEventListener('click', () => modalInstance.open());

    if (config.inlinePosition === 'before') {
      labelEl.insertBefore(btn, labelEl.firstChild);
    } else {
      labelEl.appendChild(btn);
    }
  }

  // ----------------------------------------------------------------
  // Boot — find all modals and wire them up
  // ----------------------------------------------------------------
  function init() {
    const modals = document.querySelectorAll('[data-size-chart-modal]');

    modals.forEach((modalEl) => {
      const instance = new SizeChartModal(modalEl);

      // Wire trigger buttons (both the block button and any inline ones)
      const configs = document.querySelectorAll('[data-size-chart-config]');

      configs.forEach((configEl) => {
        let config;
        try {
          config = JSON.parse(configEl.textContent);
        } catch (_) {
          return;
        }

        if (config.modalId !== modalEl.id) return;

        // Main trigger button
        const triggerEl = document.getElementById(config.triggerId);
        if (triggerEl) {
          triggerEl.addEventListener('click', () => instance.open());
        }

        // Inline injection (beside size label)
        injectInlineTrigger(config, instance);
      });
    });
  }

  // Run after DOM ready (handles both async and defer loading)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support Shopify Section rendering API (live theme editor reloads)
  document.addEventListener('shopify:section:load', init);
})();
